import { Col, Row } from 'antd';
import React, { ReactElement, useEffect } from 'react';
import Panel from '../../../shared/components/card/Panel';
import PanelCollapse from '../../../shared/components/collapse/PanelCollapse';
import PageHeader from '../../../shared/components/pageHeader/PageHeader';
import { GUTTOR_SPACE, PROFILE_CONSTANTS } from '../../../shared/constants/commonConstants';
import PDF from '../../../assets/images/svg/pdf.svg';
import { dashBoardRequest } from '../../../store/actions/leftSideBarAction';
import { FETCH_DASHBOARD } from '../../../shared/constants/urlEndPoints';
import { useDispatch } from 'react-redux';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';

const Guidelines = (): ReactElement => {
  const documents = [{
    name: 'Claim form',
    src: 'claim_form',
  }, {
    name: 'Interim bill',
    src: 'interim_bill',
  }, {
    name: 'Discharge summary',
    src: 'discharge_summary',
  }, {
    name: 'Final bill',
    src: 'final_bill',
  }, {
    name: 'Document checklist',
    src: 'document_checklist',
  }, {
    name: 'User manual',
    src: 'user_manual',
  }, {
    name: 'Fast track Maternity claims',
    src: 'maternity_automation_user_manual',
  }, {
    name: 'Fast track Cataract claims',
    src: 'cataract_automation_user_manual',
  },
  {
    name: 'NHCX Onboarding Guideline',
    src: 'nhcx_onboarding_guideline',
  },
];
  const dispatch = useDispatch();
  useEffect((): void => {
    dispatch(dashBoardRequest({
      title: PROFILE_CONSTANTS.hospitalProfile,
      url: `${process.env.REACT_APP_BASE_URL}${FETCH_DASHBOARD}`, type: PROFILE_CONSTANTS.profile,
    }));
  }, []);

  return (
    <>
      <PageHeader colSize={18} pageTitle="Guidelines" />
      <div className="ant-tabs-content-holder tab-custom-container resources">
        <Row gutter={GUTTOR_SPACE} className="guidelines width-size1">
          <Col md={16}>
            <Panel title="IHX guidelines" className="panel-resources">
              <PanelCollapse
                accordion={true}
                expandIconPosition="right"
                defaultActiveKey={[1]}
                keyName="1"
                header="CRM-IP"
              >
                <p>
                  A simplified workflow to help insurance and hospital teams
                  manage and track in-patient workflows on a single platform
                  right from intimation to settlement.
                </p>
                <p className="m-b-5">
                  <strong>Process of cashless hospitalization</strong>
                </p>
                <ol>
                  <li>
                    The Insurance card can be collected post the Doctor's
                    recommendation of admission. The Insurance Card can only be
                    used for inpatient admission which exceeds a minimum of 24
                    hours. The card cannot be used for outpatient
                    consultation/investigations. For some policies, the card can
                    also be used for daycare procedures listed in the policy
                    document.
                  </li>
                  <li>
                    The "Payer Pre-authorization" form needs to be duly filled
                    by the treating doctor and claimant. The form must be
                    submitted to the payer on the IHX portal. The
                    pre-authorization form is divided into two parts:-
                    <p>
                      - General details on the health insurance policy – to be
                      filled in by the claimant.
                    </p>
                    <p>
                      - Treatments details pertain to the treatment recommended
                      – needs to be filled in and duly signed by the doctor who
                      is treating the patient.
                    </p>
                  </li>
                  <li>
                    The form will then be uploaded to the IHX portal by the
                    hospital team. The decision on the pre-authorization request
                    will be decided by the payer/insurance company as per the
                    policy terms and conditions.
                  </li>
                  <li>
                    In case of an enhancement request, the interim/final bill
                    along with the case/discharge summary must be sent to the
                    payer through the portal. The approval status can be tracked
                    on the IHX portal.
                  </li>
                  <li>
                    In instances where further information is required for a
                    pre-authorization request, the same will be updated on the
                    IHX portal. The hospital would need to revert to the payer
                    on the portal, who would then approve the request after
                    evaluating the pre-authorization request.
                  </li>
                </ol>
                <p className="m-b-5">
                  <strong>Note: </strong>
                </p>
                <ol className="mb-0">
                  <li>
                    Payer/Insurer will not be liable for payment in case if it
                    is found that the material facts were
                    suppressed/misrepresented/not disclosed.
                  </li>
                  <li>
                    Authorization is valid for the proposed line of treatment.
                    Any change in the line of treatment must be informed
                    immediately, failing which the authorization will stand
                    canceled.
                  </li>
                  <li>
                    Expenses for irrelevant investigations/diagnostic tests will
                    be deducted. Please collect the same from the patient.
                  </li>
                  <li>
                    Expenses for the medicines/drugs for chronic ailments such
                    as diabetics, hypertension, and hyperthyroidism, etc. that
                    the patient has been taking, will be deducted.
                  </li>
                  <li>
                    Please collect the non-medical expenses/surcharges/service
                    charges/luxury taxes, etc, and co-pay as mentioned by payer
                    under "To be collected from Patient".
                  </li>
                  <li>
                    A copy of the receipt after collecting the amount from the
                    patient should be sent along with other documents,
                    otherwise, double the copay indicated above will be deducted
                    while settling the cashless claim.
                  </li>
                  <li>
                    Patients/representative's signature on the final bill is
                    mandatory; please get the signature on the final bill.
                  </li>
                  <li>
                    Please initiate the settlement process by submitting
                    relevant documents within a maximum of 7 days after the
                    patient is discharged.
                  </li>
                  <li>
                    Provide a self-attested copy of the photo ID card of the
                    patient (MANDATORY); any one of these: (a) Driving Licence
                    (b) PAN Card (c) Voter ID card (d) School/College ID card
                    (e) Passport (f) ID card issued by the employer (g) Any
                    other suitable photo identification.
                  </li>
                  <li>
                    If the bill amount exceeds INR 1 lakh, please collect a copy
                    of address proof of the primary beneficiary (Applicable to
                    Individual Mediclaim policies) such as (a) Driving Licence
                    (b) Passport (c) Voter ID card (d) Aadhar Card, etc.
                  </li>
                </ol>
              </PanelCollapse>
              <PanelCollapse
                accordion={true}
                expandIconPosition="right"
                keyName="2"
                header="CRM-OP"
              >
                <p>
                  A seamless appointment management feature that allows front
                  desk personnel to approve/reschedule appointment requests,
                  upload and manage invoices for settlements with payers.
                </p>
                <p className="m-b-5">
                  <strong>Appointment management process:</strong>
                </p>
                <p className="m-b-5">
                  All the appointments booked at your center will be visible on
                  the IHX platform to be actioned. The front desk team will have
                  an option to schedule appointments or reschedule/cancel if
                  service is not available for the preferred time.
                </p>
                <p className="m-b-5">
                  The front-desk team can also view scheduled appointments and
                  filter by today's appointments to plan the resource and time.
                  Once the member has availed of the service, the front desk
                  team will pass the message to the IHX team that the member has
                  availed of the service. If the member does not show for the
                  appointment, inform the IHX team on the portal so that the
                  appointment can be rescheduled to a time convenient for the
                  member.
                </p>
                <p className="m-b-5">
                  Once the service is completed and invoice and reports are
                  generated, please initiate settlement by uploading the invoice
                  and medical reports on the IHX portal.
                </p>
                <p className="mb-0">
                  You will be able to see the settlement status directly on the
                  portal. Once the settlement process is completed, you will be
                  able to download a settlement copy or search for transactions
                  using UTR details.
                </p>
              </PanelCollapse>
              <PanelCollapse
                accordion={true}
                expandIconPosition="right"
                keyName="3"
                header="Profile"
              >
                <p>
                  A provision for the marketing team at a hospital to showcase
                  their inventory to the end customer.
                </p>

                <p className="m-b-5">
                  <strong>Basic Profile:</strong>
                </p>
                <p className="m-b-5">
                  This section displays information about your medical
                  facilities. Members see this information when they want to
                  know more about your hospital. Complete the profile
                  information of your hospital by updating the display name and
                  highlighting the USP of your hospital along with
                  geo-coordinates of your center, This will help members to
                  reach your center without any difficulty.
                </p>
                <p>
                  Upload empanelment documents and check the status of document
                  verification directly on the IHX portal. Use calendars to
                  manage your health camp days or to manage holidays. The
                  calendar is a planning module that helps teams to manage their
                  activities and keep track of the to-do lists.
                </p>

                <p className="m-b-5">
                  <strong>Contract:</strong>
                </p>
                <p className="m-b-5">
                  Add a Hospital team who will get access to the IHX portal. IHX
                  portal access is role-based, So you will be able to choose
                  which modules the team will get access to.
                </p>
                <p>
                  If any team member leaves the organization, it is Admin's
                  responsibility to remove access to the IHX portal.
                </p>
                <p className="m-b-5">
                  <strong>Account details:</strong>
                </p>
                <p>
                  Add/update the center's registration details which will be
                  used to impanel your center to IHX and other payers.
                  Add/update account details for the payment process to
                  complete.
                </p>

                <p className="m-b-5">
                  <strong>Infrastructure details:</strong>
                </p>
                <p>
                  Add/update infrastructure details of your center like images,
                  facilities, and room details. This information is helpful for
                  empanelment to IHX and other payers, for IHX members, and your
                  CRM team during the claim's transaction.
                </p>
                <em>
                  <strong>Note:</strong> Please upload images of 650px height
                  and 350px height for best images clarity.
                </em>
              </PanelCollapse>
              <PanelCollapse
                accordion={true}
                expandIconPosition="right"
                keyName="4"
                header="Inventory"
              >
                <p>
                  A feature that helps the hospitals to configure inventory of
                  services like Doctors, Health check, IP packages, and daycare
                  services that will assist in increasing footfall.
                </p>

                <p className="m-b-5">
                  <strong>Doctors:</strong>
                </p>
                <p>
                  Configuring Doctors on the IHX platform helps the listing to
                  be available to all IHX partner members to schedule
                  appointments, and also in CRM for raising a hospitalization
                  request. Configured Doctor name and registration no are
                  available as a drop-down, which helps in faster processing of
                  a claim request.
                </p>

                <p className="m-b-5">
                  <strong>Configuring a Doctor: </strong>
                </p>
                <ol>
                  <li>
                    Add personal details like name, age, gender, and the
                    doctor's picture.
                  </li>
                  <li>
                    Add professional details like Doctor registration no,
                    qualification, and experience
                  </li>
                  <li>
                    Add Consultation details like type, Consultation fees, and
                    any fee waiver days (if applicable)
                  </li>
                  <li>
                    Add Doctors schedule for the entire week, (Note: please
                    update schedule if there is any change)
                  </li>
                  <li>
                    Submit - The submitted information will automatically be
                    available in the CRM module and sent to the IHX team for
                    verification.
                  </li>
                  <li>
                    You can see the verification status directly on the IHX
                    portal.
                  </li>
                </ol>

                <p className="m-b-5">
                  <strong>Health check packages:</strong>
                </p>
                <p className="m-b-5">
                  Configure all your hospital Health Check packages on the IHX
                  portal, this service offering will be made available to all
                  IHX partners/payers to schedule an appointment at your center.
                </p>
                <p>
                  If you do not have any Health check packages available at your
                  hospital, you can build a new health check package on the
                  portal or write to care@ihx.in to help you build packages
                  based on highly booked health check packages in your area.
                </p>

                <p className="m-b-5">
                  <strong>Configuring a health check packages:</strong>
                </p>
                <ul>
                  <li>Add a Package name</li>
                  <li>
                    Enter details applicable to members like gender, age
                    bracket.
                  </li>
                  <li>Add a brief description of the health check package</li>
                  <li>
                    Add tests by departments and consultations by departments.
                  </li>
                  <li>
                    Review the pre-loaded instructions on the right and
                    add/modify the instructions list if required.
                  </li>
                  <li>
                    Submit - The submitted information will automatically be
                    sent to the IHX team for verification.
                  </li>
                  <li>
                    You can see the verification status directly on the IHX
                    portal.
                  </li>
                </ul>
                <p className="m-b-5">
                  <strong>IP packages:</strong>
                </p>
                <p className="m-b-5">
                  Configure hospitalization packages by various room types to
                  ease the operations.
                </p>
                <p>
                  If you do not have any IP packages available at your hospital,
                  you can build a new IP package on the portal or write to
                  <a href="mailto: care@ihx.in"> care@ihx.in</a> to help you build
                  the right IP packages based on facilities available in your
                  center.
                </p>

                <p className="m-b-5">
                  <strong>Configuring IP packages:</strong>
                </p>
                <ul>
                  <li>Select a department and the procedure to be packaged.</li>
                  <li>Add a brief description of the procedure</li>
                  <li>
                    Select inclusions that are covered as part of the package.
                  </li>
                  <li>Select Length of Stay</li>
                  <li>Add package price and discount by room types. </li>
                  <li>
                    Submit - The submitted information will automatically be
                    sent to the IHX team for verification.
                  </li>
                  <li>
                    You can see the verification status directly on the IHX
                    portal.
                  </li>
                </ul>

                <p className="m-b-5">
                  <strong>Daycare Services: </strong>
                </p>
                <ul className="mb-0">
                  <li>Select a department and the procedure to be packaged.</li>
                  <li>Add a brief description of the procedure</li>
                  <li>
                    Select inclusions that are covered as part of the package.
                  </li>
                  <li>Add package price and discount.</li>
                  <li>Add multiple sessions (If applicable)</li>
                  <li>
                    Submit - The submitted information will automatically be
                    sent to the IHX team for verification.
                  </li>
                  <li>
                    You can see the verification status directly on the IHX
                    portal.
                  </li>
                </ul>
              </PanelCollapse>
              <PanelCollapse
                accordion={true}
                expandIconPosition="right"
                keyName="5"
                header="Dashboard"
              >
                <p>
                  A solution that enables finance teams to view and download
                  settlement and outstanding balances of Out-patient and
                  In-patient services.
                </p>
                <ul className="mb-0">
                  <li>
                    Hospital Business snapshot: Access a complete business
                    snapshot both In-Patient and Out-Patient business across all
                    Payers / partners.
                  </li>
                  <li>
                    Compare your center business with others in the city, by
                    department, by procedures.
                  </li>
                  <li>IHX adoption rate across various functionalities</li>
                  <li>Room occupancy rate</li>
                  <li>Cost per claim by departments, by procedures. </li>
                </ul>
              </PanelCollapse>
            </Panel>
          </Col>
          <Col md={8}>
            <Panel title="Quick Links">
              {
                documents.map(({ src, name }: any): ReactElement => <div key={src} className="quick-lins">
                  <a
                    href={`${GLOBAL_URL.REACT_APP_COMMON_BUCKET_URL}${src}.pdf`}
                    target="_blank"
                  >
                    <img src={PDF} alt="pdf" />

                    <p className="fsize_sm bold text-elipsis">{name}</p>
                  </a>
                </div>)
              }
            </Panel>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Guidelines;
